import { DateTime } from "luxon";
import React from "react";

import newsPlaceholderSvg from "@/assets/news-placeholder.svg";
import { Spacer } from "@/common/components/spacer/spacer";
import { nullIf } from "@/common/utils";
import { ReportsQueryResult } from "@/routes/reports/reports.api";

type Props =
  | {
      skeleton: true;
      className?: string;
    }
  | ({
      skeleton?: false;
      className?: string;
    } & ReportsQueryResult);

const skeletonProps = {
  className: "",
  dateFrom: "",
  dateTo: "",
  headline: "",
  image: undefined,
  skeleton: true,
  summary: "",
};

const PreviewCard = (props: Props) => {
  const { headline, summary, dateFrom, dateTo, image, skeleton, className } =
    props.skeleton ? skeletonProps : props;
  const _dateFrom = DateTime.fromISO(dateFrom).toJSDate();
  const _dateTo = DateTime.fromISO(dateTo).toJSDate();
  const dateRangeFormatter = new Intl.DateTimeFormat();
  const _image = nullIf(image?.url, "");
  const { ref: imageRef, status: imageStatus } = useFailedImage();
  const imageLoaded = _image && imageStatus !== "error";

  return (
    <article
      className={`rounded-can-2xl shadow-can-light-box flex flex-col items-center justify-center p-6 pt-0 ${className} ${
        skeleton ? "animate-pulse grayscale" : ""
      }`}
      role={skeleton ? "status" : undefined}
    >
      <Spacer className={`${imageLoaded ? "h-6" : "h-11"}`} />
      <div className="flex h-full w-full justify-center">
        <img
          className={`${
            imageLoaded
              ? "border-can-silver-cloud h-[205px] w-full rounded-lg border"
              : "h-40 w-40"
          } object-cover`}
          ref={imageRef}
          src={_image ?? newsPlaceholderSvg}
        />
      </div>
      <Spacer className={imageLoaded ? "h-4" : "h-[calc(--spacing(11)-3px)]"} />
      <div
        className={
          skeleton
            ? "flex self-stretch rounded-sm bg-gray-200"
            : "flex gap-2 self-start"
        }
      >
        <TimeSpan>
          {skeleton ? (
            <>&nbsp;</>
          ) : (
            dateRangeFormatter.formatRange(_dateFrom, _dateTo)
          )}
        </TimeSpan>
      </div>
      <Spacer className="h-2" />
      <Headline>{headline}</Headline>
      <Spacer className="h-4" />
      <Summary>{summary}</Summary>
    </article>
  );
};

const Summary = ({ children }: React.PropsWithChildren) => {
  return (
    <p className="text-can-slate-blue-gray line-clamp-2 min-h-12 self-start">
      {children}
    </p>
  );
};

const Headline = ({ children }: React.PropsWithChildren) => {
  return (
    <h2 className="text-can-midnight-steel line-clamp-2 min-h-14 self-start text-xl font-black">
      {children}
    </h2>
  );
};

const TimeSpan = ({ children }: React.PropsWithChildren) => {
  return <span className="text-can-slate-blue-gray">{children}</span>;
};

// @ts-expect-error unused component
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Tag = ({ children }: React.PropsWithChildren) => {
  return (
    <span className="from-can-tranquil-azure to-can-mystic-aqua self-start rounded-sm bg-linear-to-r px-1.5 py-1 text-xs text-white">
      {children}
    </span>
  );
};

const useFailedImage = () => {
  const ref = React.useRef<HTMLImageElement>(null);
  const [status, setStatus] = React.useState<"pending" | "error">("pending");

  React.useEffect(() => {
    if (!ref.current) {
      return;
    }

    ref.current.addEventListener("error", function () {
      this.src = newsPlaceholderSvg;
      setStatus("error");
    });
  }, []);

  return { ref, status };
};

export { PreviewCard };
